import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../../LanguageContext";
import { useUi } from "../../UiContext";
import { IconMoon, IconSearch, IconSun } from "../Icons";
import Role from "./Role";

type NavSection = {
	id: string;
	label: string;
	/** Home-only sections need `/#id`; contact lives on every page. */
	homeOnly?: boolean;
};

const NAV_SECTION_META: Array<{ id: string; homeOnly?: boolean }> = [
	{ id: "about", homeOnly: true },
	{ id: "timeline", homeOnly: true },
	{ id: "blog", homeOnly: true },
	{ id: "lab", homeOnly: true },
	{ id: "contact" },
];

function shortcutLabel() {
	if (typeof navigator === "undefined") return "Ctrl K";
	return /Mac|iPhone|iPad|iPod/i.test(navigator.platform) ? "⌘K" : "Ctrl K";
}

function scrollProgress() {
	const doc = document.documentElement;
	const max = Math.max(1, doc.scrollHeight - window.innerHeight);
	return Math.min(1, Math.max(0, window.scrollY / max));
}

function activeSectionIds(isHome: boolean, hideFooter: boolean) {
	return NAV_SECTION_META.filter((section) => {
		if (section.id === "contact") return !hideFooter;
		if (section.homeOnly) return isHome;
		return true;
	}).map((section) => section.id);
}

export default function Header() {
	const { text, lang } = useLanguage();
	const { openSearch, theme, toggleTheme } = useUi();
	const location = useLocation();
	const isHome = location.pathname === "/";
	const hideFooter =
		location.pathname === "/blog" ||
		location.pathname === "/blog/" ||
		location.pathname === "/lab" ||
		location.pathname === "/lab/" ||
		/^\/lab\/[^/]+\/?$/.test(location.pathname);
	const headerRef = useRef<HTMLElement>(null);
	const progressRef = useRef<HTMLSpanElement>(null);
	const [activeSection, setActiveSection] = useState("");

	const sections: NavSection[] = [
		{ id: "about", label: text.about.title, homeOnly: true },
		{ id: "timeline", label: text.timeline.title, homeOnly: true },
		{ id: "blog", label: text.blog.title, homeOnly: true },
		{ id: "lab", label: text.lab.title, homeOnly: true },
		{ id: "contact", label: text.footer.contact.label },
	];

	useEffect(() => {
		const header = headerRef.current;
		const progressFill = progressRef.current;
		if (!header) return;

		const sectionIds = activeSectionIds(isHome, hideFooter);

		const update = () => {
			const headerH = header.offsetHeight;
			const first = isHome
				? document.querySelector(".home-after-intro")
				: document.querySelector(".site-main");
			const footer = document.querySelector(".site-footer");

			const firstTop = first?.getBoundingClientRect().top ?? 0;
			const footerTop =
				footer?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;

			const solid = !isHome || firstTop <= headerH + 1;
			const hidden = footerTop <= headerH + 1;

			header.classList.toggle("is-solid", solid && !hidden);
			header.classList.toggle("is-hidden", hidden);

			const progress = scrollProgress();
			if (progressFill) {
				progressFill.style.transform = `scaleX(${progress})`;
			}

			const probe = headerH + 28;
			let current = "";
			for (const id of sectionIds) {
				const node = document.getElementById(id);
				if (!node) continue;
				if (node.getBoundingClientRect().top <= probe) current = id;
			}
			setActiveSection((prev) => (prev === current ? prev : current));
		};

		update();
		window.addEventListener("scroll", update, { passive: true });
		window.addEventListener("resize", update);
		return () => {
			window.removeEventListener("scroll", update);
			window.removeEventListener("resize", update);
			header.classList.remove("is-solid", "is-hidden");
		};
	}, [isHome, location.pathname, hideFooter]);

	const scrollToSection = (id: string) => {
		const node = document.getElementById(id);
		if (!node) return;

		if (id === "contact") {
			const top = node.getBoundingClientRect().top + window.scrollY;
			window.scrollTo({ top, behavior: "smooth" });
			return;
		}

		node.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const themeLabel =
		theme === "dark" ? text.header.themeToLight : text.header.themeToDark;

	return (
		<header
			ref={headerRef}
			className={`site-header ${isHome ? "is-home" : ""}`}
		>
			<div className="site-header-inner">
				<Link
					to="/"
					className="wordmark"
					data-wordmark
					aria-label={text.site.displayName.join(" ")}
				>
					<span data-wordmark-m>{text.site.wordmark[0].toLowerCase()}</span>
					<span className="wordmark-slash" data-wordmark-slash>
						{text.site.wordmark[1]}
					</span>
					<span data-wordmark-o>{text.site.wordmark[2].toLowerCase()}</span>
				</Link>
				<nav className="site-nav" aria-label={text.header.navLabel}>
					{sections.map((section) => {
						const contactOffPage = section.id === "contact" && hideFooter;
						const onPage =
							(section.id === "contact" && !hideFooter) ||
							(section.homeOnly && isHome);
						return (
							<Link
								key={section.id}
								to={{
									pathname:
										section.homeOnly || contactOffPage
											? "/"
											: location.pathname,
									hash: `#${section.id}`,
								}}
								className={
									activeSection === section.id ? "is-active" : undefined
								}
								onClick={(event) => {
									if (!onPage) return;
									event.preventDefault();
									scrollToSection(section.id);
									window.history.replaceState(null, "", `#${section.id}`);
								}}
							>
								{section.label.toLowerCase()}
							</Link>
						);
					})}
				</nav>
				<div className="header-actions">
					<Role key={lang} />
					<div className="header-tool-slot">
						{isHome ? (
							<button
								type="button"
								className="theme-toggle"
								onClick={toggleTheme}
								aria-label={themeLabel}
								title={themeLabel}
							>
								{theme === "dark" ? (
									<IconSun width="14" height="14" />
								) : (
									<IconMoon width="14" height="14" />
								)}
							</button>
						) : null}
						<button
							type="button"
							className="site-nav-search"
							onClick={openSearch}
							aria-label={text.header.searchLabel}
						>
							<IconSearch width="14" height="14" />
							<kbd className="site-nav-search-keys">{shortcutLabel()}</kbd>
						</button>
					</div>
				</div>
			</div>
			<div className="header-scroll-progress" aria-hidden="true">
				<span ref={progressRef} className="header-scroll-progress-fill" />
			</div>
		</header>
	);
}
